import { useEffect, useState } from "react";
import { BsJournalText } from "react-icons/bs";
import { ReleaseNotesModal } from "./components/ReleaseNotesModal";
import { ServerList } from "./components/ServerList";
import { ServerSelect } from "./components/ServerSelect";
import { ServerView } from "./components/ServerView";
import { loadServers } from "./data/servers";
import type { Server } from "./data/types";
import { t } from "./utils/i18n";
import { useFavorites } from "./utils/useFavorites";

export default function App() {
  const [servers, setServers] = useState<Server[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const { favoriteIds, isFavorite, toggle } = useFavorites();

  useEffect(() => {
    loadServers()
      .then((s) => {
        setServers(s);
        if (s.length > 0) {
          const favoriteSet = new Set(favoriteIds);
          const topFavorite = s
            .filter((srv) => favoriteSet.has(srv.id))
            .sort((a, b) => b.type - a.type || a.level - b.level)[0];
          const defaultServer =
            topFavorite ?? s.find((srv) => srv.name === "アルファ") ?? s[0];
          setSelectedId(defaultServer.id);
        }
      })
      .catch((e) => setError(String(e)));
  }, []);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-red-600">
        {error}
      </div>
    );
  }
  if (!servers) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        {t("loading")}
      </div>
    );
  }
  const selected = servers.find((s) => s.id === selectedId) ?? null;

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b border-slate-300 bg-slate-900 px-3 py-2 text-white">
        <h1 className="text-sm font-medium sm:text-base">
          <span className="sm:hidden">{t("appTitleShort")}</span>
          <span className="hidden sm:inline">{t("appTitleLong")}</span>
        </h1>
        <button
          type="button"
          onClick={() => setNotesOpen(true)}
          aria-label="リリースノート/今後のアップデート"
          title="リリースノート/今後のアップデート"
          className="ml-auto inline-flex items-center justify-center rounded p-1.5 text-slate-200 hover:bg-slate-700 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
        >
          <BsJournalText aria-hidden className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      </header>
      <ReleaseNotesModal
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
      />
      <div className="md:hidden border-b border-slate-300 bg-white p-2">
        <ServerSelect
          servers={servers}
          selectedId={selectedId}
          onSelect={setSelectedId}
          favoriteIds={favoriteIds}
          isFavorite={isFavorite}
          onToggleFavorite={toggle}
        />
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="hidden md:block">
          <ServerList
            servers={servers}
            selectedId={selectedId}
            onSelect={setSelectedId}
            favoriteIds={favoriteIds}
            isFavorite={isFavorite}
            onToggleFavorite={toggle}
          />
        </div>
        {selected ? (
          <ServerView server={selected} />
        ) : (
          <div className="flex flex-1 items-center justify-center text-slate-500">
            {t("selectServerPrompt")}
          </div>
        )}
      </div>
      <footer className="border-t border-slate-300 bg-white px-3 py-2 text-center text-xs text-slate-500">
        inspired by{" "}
        <a
          href="https://mmsf-hub.nomadblacky.dev/#/sss-viewer"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-600 underline hover:text-sky-700"
        >
          MMSF Hub
        </a>
        <span className="md:hidden">
          {" ｜ "}
          <a
            href="https://www.buymeacoffee.com/n4xt"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-600 underline hover:text-sky-700"
          >
            {t("supportDeveloper")}
          </a>
        </span>
      </footer>
    </div>
  );
}
